import { useState, useEffect } from 'react';
import { Calendar, MapPin, Bookmark, Clock, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { Opportunity } from '@/types';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useScrollAnimation } from '@/hooks/use-scroll-animation';

const typeLabels = {
  emploi: 'Emploi',
  stage: 'Stage',
  bourse: 'Bourse',
  formation: 'Formation',
  volontariat: 'Volontariat'
};

const typeColors = {
  emploi: 'bg-primary',
  stage: 'bg-secondary',
  bourse: 'bg-success',
  formation: 'bg-accent',
  volontariat: 'bg-destructive'
};

export function FeaturedOpportunities() {
  const [featuredOpportunities, setFeaturedOpportunities] = useState<Opportunity[]>([]);
  const sectionRef = useScrollAnimation();

  useEffect(() => {
    const fetchFeaturedOpportunities = async () => {
      try {
        const { data, error } = await supabase
          .from('opportunities')
          .select('*')
          .eq('status', 'approved')
          .order('created_at', { ascending: false })
          .limit(6);

        if (error) throw error;

        const transformedData: Opportunity[] = (data || []).map((opp: any) => ({
          id: opp.id,
          title: opp.title,
          company: opp.company,
          type: opp.type,
          location: opp.location,
          arrondissement: opp.arrondissement,
          description: opp.description,
          responsibilities: opp.responsibilities,
          requirements: opp.requirements,
          salary: opp.salary || opp.scholarship_amount,
          deadline: opp.deadline ? new Date(opp.deadline) : new Date(),
          publishedAt: new Date(opp.created_at),
          applicationUrl: opp.application_url,
          phoneNumber: opp.contact_phone,
          featured: true,
        }));

        setFeaturedOpportunities(transformedData);
      } catch (error: any) {
        console.error('Erreur lors du chargement des opportunités:', error);
      }
    };

    fetchFeaturedOpportunities();
  }, []);

  return (
    <section ref={sectionRef} className="section-padding bg-gradient-to-b from-background via-muted/10 to-background scroll-reveal">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="text-center mb-20 max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-foreground mb-8 tracking-tight leading-tight">
            Opportunités à la Une
          </h2>
          <p className="text-xl md:text-2xl text-muted-foreground font-light leading-relaxed">
            Les dernières opportunités sélectionnées spécialement pour vous
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
          {featuredOpportunities.map((opportunity, index) => (
            <Card 
              key={opportunity.id} 
              className="group glass hover-lift hover-glow border border-border/50 hover:border-primary/60 transition-all duration-500 overflow-hidden relative rounded-[2rem] animate-fade-in-up hover:rotate-1"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Featured Badge */}
              <div className="absolute top-6 right-6 z-10">
                <div className="glass-dark text-primary-foreground px-5 py-2.5 rounded-2xl text-xs font-bold shadow-2xl backdrop-blur-xl">
                  À LA UNE
                </div>
              </div>
              
              {/* Type Indicator */}
              <div className="p-2">
                <div className={`${typeColors[opportunity.type]} h-1.5 rounded-t-[1.8rem]`}></div>
              </div>
              
              <div className="p-7">
                {/* Company Logo & Info */}
                <div className="flex items-center gap-4 mb-5">
                  <div className="w-14 h-14 bg-gradient-to-br from-primary to-primary-dark rounded-2xl flex items-center justify-center text-white font-extrabold text-lg shadow-lg">
                    {opportunity.company.substring(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-muted-foreground uppercase font-semibold tracking-wide">{opportunity.company}</p>
                    <h3 className="font-bold text-xl text-foreground line-clamp-1 group-hover:text-primary transition-colors duration-300">
                      {opportunity.title}
                    </h3>
                  </div>
                  <button className="p-2.5 hover:bg-primary/10 rounded-xl transition-all duration-300 hover:scale-125 hover:rotate-12">
                    <Bookmark className="h-6 w-6 text-muted-foreground hover:text-primary transition-colors hover:fill-primary" />
                  </button>
                </div>
                
                {/* Description */}
                <p className="text-sm text-muted-foreground font-inter mb-4 line-clamp-2">
                  {opportunity.description}
                </p>
                
                {/* Meta Info */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <MapPin className="h-4 w-4" />
                    <span className="font-inter">{opportunity.arrondissement || opportunity.location}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    <span className="font-inter">
                      Publié le {format(opportunity.publishedAt, 'dd MMMM', { locale: fr })}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="h-4 w-4 text-destructive" />
                    <span className="text-destructive font-semibold font-inter">
                      Deadline: {format(opportunity.deadline, 'dd MMMM yyyy', { locale: fr })}
                    </span>
                  </div>
                </div>
                
                {/* Salary if available */}
                {opportunity.salary && (
                  <div className="mb-4">
                    <p className="text-sm font-semibold text-success font-inter">{opportunity.salary}</p>
                  </div>
                )}
                
                {/* Action Buttons */}
                <div className="flex gap-2">
                  <Link to={`/opportunity/${opportunity.id}`} className="flex-1">
                    <Button variant="default" size="sm" className="w-full hover:scale-105 transition-transform">
                      Voir détails
                    </Button>
                  </Link>
                  <Button variant="secondary" size="sm" className="hover:scale-105 transition-transform">
                    Postuler
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
        
        <div className="text-center mt-16">
          <Link to="/opportunities">
            <Button variant="outline" size="xl" className="shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300 border-2">
              Voir toutes les opportunités
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}